import isEqual from 'lodash.isequal';
import { FormEvent, useEffect, useState } from 'react';
import Alert from '@/components/Alert';
import DateRangePicker from '@/components/datepicker/DateRangePicker';
import PageHeader from '@/components/PageHeader';
import SearchForm from '@/components/search/SearchForm';
import useSearch from '@/components/search/useSearch';
import StoreSelectBox from '@/components/StoreSelectBox';
import SupplierSelectBox from '@/components/SupplierSelectBox';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import ExportOrdersButton from '@/modules/order/components/ExportOrdersButton';
import OrderStatusSelectBox from '@/modules/order/components/OrderStatusSelectBox';
import OrderTable from '@/modules/order/components/OrderTable';
import { isSupplier } from '@/modules/order/models/UserRole';
import { useSearchParams } from 'react-router-dom';

type Id = { id: string };

interface State {
  storeId: string | null;
  supplierId: string | null;
  orderStatus: string | null;
  selectedOrderIds: string[] | null;
}

const OrderList = () => {
  const { currentUser, role } = useAuth();
  const isSupplierRole = role != null && isSupplier(role);

  const [searchParams, setSearchParams] = useSearchParams();

  const { searchQuery, currentSearchQuery, page, handlePageChange, setSearchQuery } = useSearch();

  const storeIdParam = searchParams.get('storeId');
  const supplierIdParam = searchParams.get('supplierId');
  const orderStatusParam = searchParams.get('status');

  const [state, setState] = useState<State>({
    storeId: storeIdParam,
    supplierId: supplierIdParam,
    orderStatus: orderStatusParam,
    selectedOrderIds: null,
  });

  const search = (
    searchQuery: string | null,
    storeId: string | null,
    supplierId: string | null,
    orderStatus: string | null,
  ) => {
    setSearchParams(
      new URLSearchParams({
        page: '1',
        query: searchQuery || '',
        storeId: storeId || '',
        supplierId: supplierId || '',
        status: orderStatus || '',
      }),
    );
  };

  const { storeId, supplierId, orderStatus, selectedOrderIds } = state;

  const handleStoreSelect = (store?: Id | null) => {
    setState((prevState) => ({
      ...prevState,
      storeId: store?.id || null,
    }));
    search(currentSearchQuery, store?.id || null, supplierId, orderStatus);
  };

  const handleSupplierSelect = (supplier?: Id | null) => {
    setState((prevState) => ({
      ...prevState,
      supplierId: supplier?.id || null,
    }));
    search(currentSearchQuery, storeId, supplier?.id || null, orderStatus);
  };

  const handleOrderStatusSelect = (orderStatus?: Id | null) => {
    setState((prevState) => ({
      ...prevState,
      orderStatus: orderStatus?.id || null,
    }));
    search(currentSearchQuery, storeId, supplierId, orderStatus?.id || null);
  };

  const handleChangeSelectedItems = (orderIds: string[]) => {
    setState((prevState) => {
      if (isEqual(prevState.selectedOrderIds, orderIds)) {
        return prevState;
      }

      return {
        ...prevState,
        selectedOrderIds: orderIds,
      };
    });
  };

  const handleSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    search(currentSearchQuery, storeId, supplierId, orderStatus);
  };

  useEffect(() => {
    setState((prevState) => {
      if (supplierIdParam === prevState.supplierId && orderStatusParam === prevState.orderStatus) {
        return prevState;
      }

      return {
        ...prevState,
        supplierId: supplierIdParam,
        orderStatus: orderStatusParam,
      };
    });
  }, [supplierIdParam, orderStatusParam]);

  return (
    <>
      <PageHeader
        title="Orders"
        alert={
          isSupplierRole && (
            <Alert
              type="info"
              message="&#128075; Welcome! How can we assist you today? It's great to have you here!"
            />
          )
        }
      >
        <ExportOrdersButton
          searchQuery={searchQuery}
          supplierId={isSupplierRole ? currentUser().supplierId : supplierId}
          orderStatus={orderStatus}
          orderIds={selectedOrderIds}
        />
      </PageHeader>

      <div className="flex flex-col gap-4">
        <div className="flex-1">
          <SearchForm
            as="Fragment"
            value={searchQuery}
            onSubmit={handleSearchSubmit}
            onChange={setSearchQuery}
            placeholder="Search by order ID, e.g. 123456"
          />
        </div>
        <div className="flex flex-col lg:flex-row gap-4 mb-8 lg:items-end">
          <div className="sm:w-96 lg:w-80">
            {!isSupplierRole && (
              <SupplierSelectBox value={supplierId} onSelect={handleSupplierSelect} />
            )}

            {isSupplierRole && (
              <StoreSelectBox
                value={storeId}
                label="Retailer:"
                placeholder="Select retailer"
                onSelect={handleStoreSelect}
              />
            )}
          </div>
          <div className="sm:w-96 lg:w-80">
            <OrderStatusSelectBox
              value={orderStatus}
              isSupplier={isSupplierRole}
              onSelect={handleOrderStatusSelect}
            />
          </div>
          <div className="sm:w-96 lg:w-80">
            <DateRangePicker
              onChange={(value) => console.log(value)}
              label="Date:"
              locale="en-GB"
            />
          </div>
        </div>
      </div>

      <OrderTable
        searchQuery={searchQuery}
        page={page}
        onChangePage={handlePageChange}
        storeId={storeId}
        supplierId={supplierId}
        status={orderStatus}
        isSupplier={isSupplierRole}
        onChangeCheckedItems={handleChangeSelectedItems}
      />
    </>
  );
};

export default OrderList;
